import type { ReactNode } from 'react';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
}

/**
 * A labelled radio-style row of segmented buttons — exactly one active. Shared
 * by the item-master AS/400 selectors (export format, view column group) so the
 * button-group markup lives in one place.
 */
export function SegmentedButtonRow<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: ReactNode;
  options: readonly SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide whitespace-nowrap w-28">
        {label}
      </span>
      <div className="flex rounded-md border overflow-hidden text-xs">
        {options.map((option, index) => {
          const isActive = value === option.value;
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={isActive}
              className={`px-3 py-1 transition-colors${index > 0 ? ' border-l' : ''}${isActive ? ' bg-primary text-primary-foreground' : ' bg-background text-foreground hover:bg-muted'}`}
              onClick={() => onChange(option.value)}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
